import { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { pageStyles } from "./pageStyles";
import { SECTIONS } from "./sections";
import BrandMark from "../components/BrandMark";

function NavGlyph({ id, active = false }) {
  const shared = {
    width: 16,
    height: 16,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 1.7,
    strokeLinecap: "round",
    strokeLinejoin: "round",
    "aria-hidden": "true",
    style: { flex: "0 0 auto", color: active ? "#a6c8ff" : "currentColor" },
  };
  const shapes = {
    chart: <><path d="M4 19V5M4 19h17" /><path d="M7 15l3-4 3 2 5-7" /><path d="M16 6h2v2" /></>,
    marketdepth: <><path d="M4 6h16M7 10h13M5 14h15M8 18h12" /><path d="M4 4v4M6 8v4M3 12v4M7 16v4" /></>,
    python: <><path d="m8 7-4 5 4 5M16 7l4 5-4 5" /><path d="M13 5 11 19" /></>,
    backtest: <><path d="M4 19V5M4 19h17" /><path d="M7 15h3l2-6 3 8 2-5h3" /></>,
    scanner: <><circle cx="10.5" cy="10.5" r="6.5" /><path d="m16 16 4 4M8 10h5M10.5 7.5v5" /></>,
    plugins: <><path d="M9 4v4M15 4v4M5 9h14v3a3 3 0 0 1-3 3h-1v5h-6v-5H8a3 3 0 0 1-3-3V9Z" /></>,
  };
  return <svg {...shared}>{shapes[id] || shapes.chart}</svg>;
}

const linkStyle = ({ isActive }) => ({
  ...pageStyles.navLink,
  ...(isActive ? pageStyles.navLinkActive : {}),
});

export default function NavBar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { pathname, hash } = useLocation();
  const [profileOpen, setProfileOpen] = useState(false);
  const profileRef = useRef(null);
  const onApp = pathname === "/app";
  const selectedSection = hash.replace(/^#/, "");
  const active = SECTIONS.some((section) => section.id === selectedSection) ? selectedSection : "chart";

  const handleLogout = () => {
    setProfileOpen(false);
    logout();
    navigate("/");
  };

  useEffect(() => {
    const onPointerDown = (event) => {
      if (!profileRef.current?.contains(event.target)) setProfileOpen(false);
    };
    const onKeyDown = (event) => { if (event.key === "Escape") setProfileOpen(false); };
    document.addEventListener("mousedown", onPointerDown);
    window.addEventListener("keydown", onKeyDown);
    return () => { document.removeEventListener("mousedown", onPointerDown); window.removeEventListener("keydown", onKeyDown); };
  }, []);

  const tabStyle = (on) => ({
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    flex: "0 0 auto",
    minHeight: 36,
    padding: "0 11px",
    border: `1px solid ${on ? "rgba(110,168,255,0.24)" : "transparent"}`,
    borderRadius: 9,
    background: on ? "linear-gradient(135deg, rgba(110,168,255,0.17), rgba(110,168,255,0.075))" : "transparent",
    color: on ? pageStyles.navLinkActive.color : pageStyles.navLink.color,
    fontFamily: pageStyles.navLink.fontFamily,
    fontSize: "0.76rem",
    fontWeight: on ? 680 : 560,
    letterSpacing: "0.025em",
    lineHeight: 1,
    textDecoration: "none",
    whiteSpace: "nowrap",
    cursor: "pointer",
    boxShadow: on ? "inset 0 1px 0 rgba(255,255,255,0.08), 0 3px 12px rgba(0,0,0,0.16)" : "none",
    transition: "background 150ms ease, border-color 150ms ease, color 150ms ease",
  });

  return (
    <header style={pageStyles.nav}>
      <Link to="/" style={{ ...pageStyles.navBrand, display: "flex", alignItems: "center", minWidth: 0 }} aria-label="Lightweight Terminal home"><BrandMark /></Link>
      <nav aria-label="Sections" style={pageStyles.navCenter}>
        {user &&
          SECTIONS.map((s) =>
            onApp ? (
              <button
                key={s.id}
                type="button"
                aria-current={active === s.id ? "true" : undefined}
                onClick={() => navigate(`/app#${s.id}`)}
                style={tabStyle(active === s.id)}
                title={s.label}
              >
                <NavGlyph id={s.id} active={active === s.id} />
                <span>{s.label}</span>
              </button>
            ) : (
              <Link key={s.id} to={`/app#${s.id}`} style={tabStyle(false)}>
                <NavGlyph id={s.id} />
                <span>{s.label}</span>
              </Link>
            )
          )}
        {user && <NavLink to="/scanner" style={({ isActive }) => tabStyle(isActive)}><NavGlyph id="scanner" /><span>Scanner</span></NavLink>}
        {user && <NavLink to="/plugins" style={({ isActive }) => tabStyle(isActive)}><NavGlyph id="plugins" /><span>Plugins</span></NavLink>}
      </nav>
      <nav aria-label="Account" style={pageStyles.navLinks}>
        {user ? (
          <>
            {user.is_admin && <NavLink to="/admin" style={linkStyle}>Admin</NavLink>}
            {user.is_guest && <NavLink to="/login" style={linkStyle}>Sign in</NavLink>}
            <div ref={profileRef} style={{ position: "relative", display: "flex", alignItems: "stretch" }}>
              <button type="button" aria-haspopup="menu" aria-expanded={profileOpen} onClick={() => setProfileOpen((open) => !open)} style={{ ...pageStyles.navLink, fontWeight: 600 }}>
                {user.is_guest ? "Guest · 1 hour" : user.display_name || "Profile"}<span aria-hidden="true" style={{ marginLeft: 6, fontSize: 10, opacity: 0.7 }}>{profileOpen ? "▲" : "▼"}</span>
              </button>
              {profileOpen && <div role="menu" style={{ position: "absolute", right: 0, top: "calc(100% - 2px)", minWidth: 190, padding: 6, borderRadius: 10, border: `1px solid ${pageStyles.nav.borderBottom || "rgba(255,255,255,.14)"}`, background: "rgba(10,12,16,.97)", boxShadow: "0 12px 30px rgba(0,0,0,.38)", backdropFilter: "blur(16px)", zIndex: 70 }}>
                {user.is_guest && <div style={{ padding: "8px 10px", color: "#9097a3", fontSize: 11 }}>Trial ends {user.guest_expires_at ? new Date(user.guest_expires_at).toLocaleString() : "in 1 hour"}</div>}
                <NavLink to="/marketplace" role="menuitem" onClick={() => setProfileOpen(false)} style={({ isActive }) => ({ ...pageStyles.navLink, minHeight: 38, width: "100%", boxSizing: "border-box", borderRadius: 7, justifyContent: "flex-start", ...(isActive ? pageStyles.navLinkActive : {}) })}>Marketplace</NavLink>
                <NavLink to="/plans" role="menuitem" onClick={() => setProfileOpen(false)} style={({ isActive }) => ({ ...pageStyles.navLink, minHeight: 38, width: "100%", boxSizing: "border-box", borderRadius: 7, justifyContent: "flex-start", ...(isActive ? pageStyles.navLinkActive : {}) })}>Plans & billing</NavLink>
                <NavLink to={user.is_guest ? "/signup" : "/profile"} role="menuitem" onClick={() => setProfileOpen(false)} style={({ isActive }) => ({ ...pageStyles.navLink, minHeight: 38, width: "100%", boxSizing: "border-box", borderRadius: 7, justifyContent: "flex-start", ...(isActive ? pageStyles.navLinkActive : {}) })}>{user.is_guest ? "Create an account" : "Profile settings"}</NavLink>
                {user.is_guest && <NavLink to="/login" role="menuitem" onClick={() => setProfileOpen(false)} style={({ isActive }) => ({ ...pageStyles.navLink, minHeight: 38, width: "100%", boxSizing: "border-box", borderRadius: 7, justifyContent: "flex-start", ...(isActive ? pageStyles.navLinkActive : {}) })}>Sign in to an existing account</NavLink>}
              </div>}
            </div>
            <button
              type="button"
              onClick={handleLogout}
              aria-label="Log out"
              style={{
                ...pageStyles.navLink,
                minHeight: 32,
                padding: "0 10px",
                border: "1px solid rgba(255, 125, 140, 0.24)",
                borderRadius: 8,
                color: "#ff9ca7",
                fontSize: "0.76rem",
                cursor: "pointer",
              }}
            >
              Log out
            </button>
          </>
        ) : (
          <>
            <NavLink to="/marketplace" style={linkStyle}>Marketplace</NavLink>
            <NavLink to="/plans" style={linkStyle}>Plans</NavLink>
            <NavLink to="/login" style={linkStyle}>Sign in</NavLink>
            <NavLink to="/signup" style={linkStyle}>Sign up</NavLink>
          </>
        )}
      </nav>
    </header>
  );
}
